// ============================================================
// AssistantBar-Engine — die Gemini-Live-Sprachsitzung.
//
// 1:1 aus AssistantBar.js (Vanilla) verschoben: gleiche Timeouts, gleiche
// Gain-Werte, gleiches prefixPaddingMs, gleiche Ereignis-Reihenfolge.
// DOM-Zugriffe des alten Codes sind Engine-Zustand geworden:
//   micBtn.classList.add('ab-recording')  → e.micRecording = true
//   voiceHint.textContent = …             → e.setHint(…)
//   orbWrap                               → e.host.getOrbWrap()
// ============================================================
import SiriWave from 'siriwave';
import { GoogleGenAI, Modality } from '@google/genai';
import { t, getLanguage } from '../../../i18n/index.js';
// @ts-expect-error — i18n/languages.js ist reines JS ohne Typen
import { APP_LANGUAGES } from '../../../i18n/languages.js';
import { getVoiceInteraction } from '../../../core/voiceInteraction.ts';
import { authedFetch, errDetail, VOICE_TOKEN_URL } from './api.ts';
import { abToB64, b64ToAb, voiceVolume } from './audio.ts';
import { activityLabel } from './activity.ts';
import { runVoiceTool } from './tools.ts';
import type { AssistantEngine } from './AssistantEngine.ts';
import { getOpenDocument } from '../../../core/uiCommands.ts';
// @ts-expect-error — core/voiceTargets.js ist reines JS ohne Typen
import { collectVoiceTargets } from '../../../core/voiceTargets.js';

/* eslint-disable @typescript-eslint/no-explicit-any */

export function stopPlayback(e: AssistantEngine): void {
  for (const s of e.playSources) { try { s.stop(); } catch { /* schon gestoppt */ } }
  e.playSources = [];
  if (e.playCtx) e.playTime = e.playCtx.currentTime;
}

// ── «Auflegen» nach der Verabschiedung ────────────────────────
// end_voice_session (engine/tools.ts) setzt nur ein Flag. Hier wird daraus
// ein Stopp — aber erst, wenn das «Tschüss» auch wirklich VERKLUNGEN ist.
// e.playTime ist der Zeitpunkt, an dem der zuletzt eingeplante Audio-Puffer
// endet; die Differenz zur Uhr des Kontexts ist also exakt die Restspielzeit.
function endAfterFarewell(e: AssistantEngine, gen: number): void {
  if (e.voiceEndTimer) { clearTimeout(e.voiceEndTimer); e.voiceEndTimer = null; }
  const rest = e.playCtx ? Math.max(0, e.playTime - e.playCtx.currentTime) : 0;
  e.vdbg('bye: stop in ' + rest.toFixed(1) + 's');
  e.voiceEndTimer = setTimeout(() => {
    e.voiceEndTimer = null;
    if (gen !== e.voiceGen) return;   // längst eine andere Sitzung
    if (!e.voiceEndAfterTurn) return; // Nutzer hat doch weitergeredet
    stopVoice(e);
  }, rest * 1000 + 250);
}

// Notbremse: schweigt das Modell nach dem Auflegen-Werkzeug einfach (keine
// Verabschiedung, kein turnComplete), soll das Mikrofon trotzdem ausgehen —
// sonst lauscht es ewig weiter, obwohl der Nutzer sich verabschiedet hat.
const VOICE_END_FALLBACK_MS = 15000;
function armVoiceEndFallback(e: AssistantEngine, gen: number): void {
  if (e.voiceEndTimer) return; // schon geplant (normaler Weg über turnComplete)
  e.voiceEndTimer = setTimeout(() => {
    e.voiceEndTimer = null;
    if (gen !== e.voiceGen || !e.voiceEndAfterTurn) return;
    e.vdbg('bye: Notbremse');
    stopVoice(e);
  }, VOICE_END_FALLBACK_MS);
}

// Verabschiedung zurücknehmen — der Nutzer redet doch weiter.
export function cancelVoiceEnd(e: AssistantEngine): void {
  if (e.voiceEndTimer) { clearTimeout(e.voiceEndTimer); e.voiceEndTimer = null; }
  e.voiceEndAfterTurn = false;
}

function playDest(e: AssistantEngine): GainNode {
  if (!e.playGain) {
    e.playGain = e.playCtx!.createGain();
    e.playGain.gain.value = voiceVolume();
    const comp = e.playCtx!.createDynamicsCompressor();
    e.playGain.connect(comp); comp.connect(e.playCtx!.destination);
  }
  return e.playGain;
}

export function playChunk(e: AssistantEngine, b64: string): void {
  // !voiceActive: nach dem Stopp darf KEIN Chunk mehr spielen — ohne diese
  // Sperre erschuf die Zeile darunter einen neuen playCtx und die Stimme
  // redete nach dem Stopp einfach weiter.
  if (e.formOpen || !e.voiceActive) return;
  try {
    if (!e.playCtx) { e.playCtx = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 }); e.playTime = e.playCtx.currentTime; }
    // iOS: falls der Kontext doch suspended ist, weckt resume() ihn —
    // besser spät hörbar als still verpuffen.
    if (e.playCtx.state === 'suspended') e.playCtx.resume().catch(() => {});
    const buf = b64ToAb(b64); const i16 = new Int16Array(buf);
    const f32 = new Float32Array(i16.length);
    for (let i = 0; i < i16.length; i++) f32[i] = i16[i] / 32768;
    const ab = e.playCtx.createBuffer(1, f32.length, 24000);
    ab.getChannelData(0).set(f32);
    const srcN = e.playCtx.createBufferSource(); srcN.buffer = ab; srcN.connect(playDest(e));
    const now = e.playCtx.currentTime; if (e.playTime < now) e.playTime = now;
    e.playSources.push(srcN);
    srcN.onended = () => { e.playSources = e.playSources.filter((x) => x !== srcN); };
    srcN.start(e.playTime); e.playTime += ab.duration;
  } catch (err) { console.error('[voice] play', err); }
}

export function ensureSiri(e: AssistantEngine): void {
  if (e.siri) return;
  if (!e.voiceActive || e.headless) return;
  const orbWrap = e.host ? e.host.getOrbWrap() : null;
  if (!orbWrap) {
    // ANPASSUNG (Island-Architektur): früher existierte der Orb-Container
    // immer schon im Template — jetzt committet React ihn erst kurz nach
    // openPanel('voice'). Bis dahin in kleinen Schritten nachfassen; das
    // alte setTimeout(…, 30) bleibt der erste Versuch.
    if (e.siriRetry < 10) { e.siriRetry += 1; setTimeout(() => ensureSiri(e), 50); }
    return;
  }
  e.siriRetry = 0;
  e.siri = new SiriWave({
    container: orbWrap,
    width: orbWrap.clientWidth || 280,
    height: orbWrap.clientHeight || 140,
    style: 'ios9',
    autostart: true,
    speed: 0.12,
    amplitude: 0.4,
  });
}

export function tick(e: AssistantEngine): void {
  e.rafId = requestAnimationFrame(() => tick(e));
  if (!e.analyser || !e.siri) return;
  e.analyser.getByteFrequencyData(e.dataArr!);
  let sum = 0;
  for (let i = 0; i < e.dataArr!.length; i++) sum += e.dataArr![i];
  const level = sum / e.dataArr!.length / 255;
  e.siri.setAmplitude(Math.min(4, 0.15 + level * 8));
}

// ── Bildschirm-Spiegel ──────────────────────────────────────
// Mohammads Wunsch: das Modell soll VOR jeder Antwort wissen, was auf dem
// Bildschirm steht — es riet sonst («Formular ist offen», obwohl längst zu).
// Umsetzung nach seiner Idee: jede Sekunde den Bildschirm einsammeln, mit dem
// zuletzt Gesendeten vergleichen, NUR Änderungen schicken. turnComplete:false
// macht den Block zu reinem Kontext: er löst keine Antwort aus und taucht
// nicht in der Transkription (dem sichtbaren Verlauf) auf.
function screenSnapshot(): string {
  const doc = getOpenDocument();
  const targets = collectVoiceTargets() as Array<{ name: string; label?: string; selected?: boolean }>;
  const lines = targets.map((t) =>
    (t.label && t.label !== t.name ? `${t.name} (${t.label})` : t.name) + (t.selected ? ' [selected]' : ''));
  return (
    'SCREEN (app signal — the user did NOT say this; never answer it, never mention it; it just tells you what is on the screen RIGHT NOW): '
    + (doc ? `open document id: ${doc}. ` : 'no document open. ')
    + (lines.length ? `Tappable elements: ${lines.join('; ')}.` : 'No tappable elements reported.')
  );
}

function startScreenWatch(e: AssistantEngine, gen: number): void {
  stopScreenWatch(e);
  e.screenLastSent = '';
  e.screenTimer = setInterval(() => {
    if (gen !== e.voiceGen || !e.session) return;
    try {
      const snap = screenSnapshot();
      if (snap === e.screenLastSent) return;
      e.screenLastSent = snap;
      e.session.sendClientContent({ turns: [{ role: 'user', parts: [{ text: snap }] }], turnComplete: false });
    } catch { /* Session gerade am Schliessen — nächster Tick prüft gen erneut */ }
  }, 1000);
}

export function stopScreenWatch(e: AssistantEngine): void {
  if (e.screenTimer) { clearInterval(e.screenTimer); e.screenTimer = null; }
  e.screenLastSent = '';
}

export async function startVoice(e: AssistantEngine): Promise<void> {
  // Diese Sitzung merkt sich ihre Generation. Drückt der Nutzer Stopp,
  // während unten noch ein await läuft, stimmt `gen` nicht mehr mit
  // `voiceGen` überein — dann bricht der jeweilige Schritt ab, statt eine
  // tote Sitzung fertig aufzubauen.
  e.voiceGen += 1;
  const gen = e.voiceGen;
  e.voiceActive = true;
  // iOS-Safari-Falle: AudioContexte MÜSSEN synchron innerhalb der Nutzer-
  // Geste entstehen UND resumed werden — nach dem ersten `await` ist die
  // «user activation» weg, der Kontext bleibt dann für immer `suspended`:
  // das Mikrofon liefert keine Frames (Worklet still) und das Antwort-
  // Audio bleibt stumm. Darum BEIDE Kontexte hier zuerst, vor jedem await.
  try {
    if (!e.audioCtx) e.audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 16000 });
    e.audioCtx.resume().catch(() => {});
    if (!e.playCtx) { e.playCtx = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 }); e.playTime = e.playCtx.currentTime; }
    e.playCtx.resume().catch(() => {});
  } catch (err: any) { e.vdbg('ctx init: ' + (err && err.message ? err.message : '')); }
  // Modus-Popup beim Start bewusst NICHT mehr zeigen (Mohammads Wunsch,
  // 2026-08-11): die Sitzung startet still im gespeicherten Modus.
  // showModePop bleibt am Host, falls der Umschalter je zurückkommt.
  if (!e.headless) e.openPanel('voice');
  // Eine Sprachsitzung bekommt einen EIGENEN Verlauf — sie wird nicht an einen
  // offenen Text-Chat angehängt. Der laufende Chat wird vorher gesichert.
  await e.saveChat();
  e.chatHistory = [];
  e.currentChatId = null;
  e.currentChatKind = 'voice';
  e.vtUserBuf = ''; e.vtModelBuf = ''; e.vtUserLang = ''; e.vtModelLang = '';
  e.micRecording = true;
  e.micConnecting = true;
  // Hinweis zurücksetzen: sonst steht die Fehlermeldung der letzten Sitzung
  // («Mikrofonzugriff verweigert») noch da — früher überschrieb sie die Debug-Spur.
  e.setHint(null); // null ⇒ die UI zeigt t('assistant.listening')
  e.vdbg('connecting');
  e.notifyHeadless('connecting');
  if (!e.headless) setTimeout(() => ensureSiri(e), 30);

  // 1) token az backend begir
  let token: string;
  let userNameForGreeting = '';
  try {
    // lang: die UI-Sprache bestimmt die GESPRÄCHSSPRACHE (der Server baut
    // daraus die LANGUAGE-Zeile des System-Prompts der Live-Session).
    // interaction: der gewählte Modus (Mic-Toggle) wird serverseitig in den
    // System-Prompt der Live-Session eingefroren — darum hier, vor connect.
    const r = await authedFetch(VOICE_TOKEN_URL, {
      method: 'POST', body: JSON.stringify({ lang: getLanguage(), interaction: getVoiceInteraction() }),
    });
    const d = await r.json();
    if (!d.success) throw new Error(errDetail(d.error) || 'token failed'); e.vdbg('token ok');
    token = d.token;
    userNameForGreeting = d.user_name || '';
    if (gen !== e.voiceGen) return; // gestoppt, während der Token unterwegs war
  } catch (err: any) {
    e.setHint(t('assistant.connectionFailed', { detail: err.message }));
    e.micRecording = false; e.micConnecting = false; e.emit();
    e.notifyHeadless('error');
    e.headless = false; e.onHeadlessState = null; e.onHeadlessActivity = null;
    return;
  }

  // 2) mic begir (audioCtx existiert schon aus der Geste oben; hier nur
  //    Fallback, falls die Erstellung dort scheiterte)
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    if (gen !== e.voiceGen) {
      // gestoppt, während der Mikrofon-Dialog offen war — Spur sofort
      // wieder freigeben, sonst leuchtet das Aufnahme-Lämpchen weiter
      try { stream.getTracks().forEach((tr) => tr.stop()); } catch { /* wie zuvor */ }
      return;
    }
    e.micStream = stream;
    if (!e.audioCtx) e.audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 16000 });
    e.audioCtx.resume().catch(() => {});
  } catch {
    e.setHint(t('assistant.micDenied'));
    e.micRecording = false; e.micConnecting = false; e.emit();
    e.notifyHeadless('error');
    e.headless = false; e.onHeadlessState = null; e.onHeadlessActivity = null;
    return;
  }

  // 3) be Live vasl sho (token = apiKey, v1alpha)
  try {
    const ai = new GoogleGenAI({ apiKey: token, httpOptions: { apiVersion: 'v1alpha' } });
    // WICHTIG: erst in eine LOKALE Variable — würde direkt `session`
    // zugewiesen, könnte ein während des Stopps noch laufendes connect()
    // nachträglich die (bereits neue) Sitzung überschreiben.
    const s = await ai.live.connect({
      // Gemini 3.8 Live — MUSS identisch zu LIVE_MODEL in voice.routes.ts sein
      // (der Server sperrt das Modell in den liveConnectConstraints des Tokens).
      model: 'gemini-3.8-live',
      config: {
        responseModalities: [Modality.AUDIO],
        speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Kore' } } },
        // Gesprächsverlauf: Gemini schreibt beide Seiten als Text mit.
        // Leeres Objekt = Sprache wird selbst erkannt (Produkt ist mehrsprachig,
        // also NICHT auf Deutsch festnageln). Muss auch serverseitig in den
        // liveConnectConstraints stehen (voice.routes.ts) — sonst kommt kein Text.
        inputAudioTranscription: {},
        outputAudioTranscription: {},
        // Barge-in unempfindlicher: vorher hat schon Umgebungsgeräusch die
        // Stimme abgewürgt («jemand redet» → interrupted). Der wirksame Hebel
        // ist prefixPaddingMs — so lange muss ERKANNTE SPRACHE andauern, bevor
        // «Sprechbeginn» gilt (≈ zwei-drei Worte). START_SENSITIVITY_LOW ist
        // laut SDK ohnehin der Default, steht hier nur explizit. Ende-Erkennung
        // bleibt Standard (sonst antwortet er träge). Muss auch in den
        // liveConnectConstraints stehen (voice.routes.ts).
        realtimeInputConfig: {
          automaticActivityDetection: {
            startOfSpeechSensitivity: 'START_SENSITIVITY_LOW' as any,
            prefixPaddingMs: 800,
          },
        },
      },
      callbacks: {
        onopen: async () => {
          if (gen !== e.voiceGen) return; // Sitzung wurde inzwischen gestoppt
          e.micConnecting = false; e.emit();
          e.vdbg('LIVE open - speak');
          e.notifyHeadless('live');
          // stream-e mic
          e.micSource = e.audioCtx!.createMediaStreamSource(e.micStream!);
          // AudioWorklet: pardazesh-e seda roye thread-e joda (main thread azad)
          try {
            await e.audioCtx!.audioWorklet.addModule('/pcm-processor.js');
            e.processor = new AudioWorkletNode(e.audioCtx!, 'pcm-processor');
            e.processor.port.onmessage = (ev) => {
              if (!e.session || e.micPaused) return;
              try { e.session.sendRealtimeInput({ audio: { mimeType: 'audio/pcm;rate=16000', data: abToB64(ev.data) } }); } catch { /* wie zuvor */ }
            };
            e.micSource.connect(e.processor);
          } catch (werr: any) {
            e.vdbg('worklet fail: ' + (werr && werr.message ? werr.message : ''));
          }
          // analyser baraye moj
          e.analyser = e.audioCtx!.createAnalyser(); e.analyser.fftSize = 256;
          e.micSource.connect(e.analyser); e.dataArr = new Uint8Array(e.analyser.frequencyBinCount);
          tick(e);
          // kickoff: assistant khodash aval salam konad (ba esm-e karbar)
          // ZUR SPRACHE: der Gruss folgt der UI-Sprache (vorher fest Deutsch —
          // eine englische/persische Oberflaeche wurde deutsch begruesst).
          // Der englische Sprachname kommt aus dem Sprachregister, weil Modelle
          // rohe BCP-47-Codes unzuverlaessig auflösen (wie im Backend-Prompt).
          // Wortlaut «SESSION START …» ist ABGESPROCHEN mit der Regel
          // «SESSION START — ALWAYS GREET FIRST» im System-Prompt: seit der
          // strengen Scope-Regel hätte das Modell eine freie Meta-Anweisung
          // sonst als Off-Topic behandeln und einfach schweigen können.
          try {
            const nm = (userNameForGreeting || '').trim();
            const langName = (APP_LANGUAGES.find((l: any) => l.code === getLanguage()) || {}).english || 'German';
            const greetMsg = nm
              ? ('SESSION START — the user just opened the microphone. Greet them now in ' + langName + ' by their name "' + nm + '" and ask how you can help. One short line, nothing else.')
              : ('SESSION START — the user just opened the microphone. Greet them now in ' + langName + ' and ask how you can help. One short line, nothing else.');
            e.session.sendClientContent({ turns: [{ role: 'user', parts: [{ text: greetMsg }] }], turnComplete: true });
          } catch (ge: any) { e.vdbg('kickoff fail: ' + (ge && ge.message ? ge.message : '')); }
          // Bildschirm-Spiegel starten (1s-Diff, sendet nur Änderungen)
          startScreenWatch(e, gen);
        },
        onmessage: async (msg: any) => {
          if (gen !== e.voiceGen) return; // Nachzügler einer gestoppten Sitzung
          // sedaye javab
          if (msg.serverContent?.interrupted) {
            e.vdbg('interrupted'); stopPlayback(e);
            // Der Nutzer redet doch weiter — also kein Auflegen. Bewusst NUR
            // dieses Signal: die Transkription läuft asynchron, sein eigenes
            // «Tschüss» könnte also NACH dem Werkzeugaufruf eintrudeln und
            // eine Prüfung auf «Nutzer spricht» fälschlich auslösen.
            if (e.voiceEndAfterTurn) { cancelVoiceEnd(e); e.vdbg('bye: abgebrochen'); }
          }
          // ── Gesprächsverlauf mitschreiben ──────────────────────────
          // Achtung: die Transkription läuft NICHT synchron zum modelTurn
          // (steht so in der Gemini-Doku) — darum eigene Puffer statt
          // Anhängen an den Audio-Zweig.
          const sc = msg.serverContent;
          if (sc?.inputTranscription) {
            if (sc.inputTranscription.text) e.vtUserBuf += sc.inputTranscription.text;
            if (sc.inputTranscription.languageCode) e.vtUserLang = sc.inputTranscription.languageCode;
            if (sc.inputTranscription.finished) e.flushVoiceText('user');
          }
          if (sc?.outputTranscription) {
            if (sc.outputTranscription.text) e.vtModelBuf += sc.outputTranscription.text;
            if (sc.outputTranscription.languageCode) e.vtModelLang = sc.outputTranscription.languageCode;
            if (sc.outputTranscription.finished) e.flushVoiceText('model');
          }
          // Turn zu Ende (oder abgebrochen): Reste sichern und laufend speichern,
          // damit ein geschlossener Tab die Sitzung nicht verliert.
          if (sc?.turnComplete || sc?.interrupted) {
            if (e.flushVoiceAll()) { e.saveChat('voice'); }
          }
          // Verabschiedung ist ausgesprochen → Mikrofon aus. Der Aufruf von
          // end_voice_session passiert weiter unten in DIESER Funktion, also
          // erst nach dieser Prüfung: ein turnComplete, das im selben Paket
          // wie der Werkzeugaufruf steckt, legt deshalb nicht vorzeitig auf.
          if (sc?.turnComplete && e.voiceEndAfterTurn) endAfterFarewell(e, gen);
          const parts = msg.serverContent?.modelTurn?.parts || [];
          if (parts.some((p: any) => p.inlineData)) e.vdbg('audio in');
          for (const p of parts) {
            if (p.inlineData?.data) playChunk(e, p.inlineData.data);
          }
          // function call
          if (msg.toolCall?.functionCalls) {
            e.vdbg('toolCall');
            const responses = [];
            for (const fc of msg.toolCall.functionCalls) {
              e.setActivity(activityLabel(fc.name));
              const r = await runVoiceTool(e, fc.name, fc.args);
              e.recordToolCall(fc.name, fc.args, r);
              responses.push({ id: fc.id, name: fc.name, response: r });
            }
            e.setActivity(null);
            e.saveChat('voice');
            // Während der (mitunter langen) Tool-Ausführung kann die Sitzung
            // gestoppt worden sein — dann gibt es niemanden mehr für die Antwort.
            if (gen !== e.voiceGen || !e.session) return;
            try { e.session.sendToolResponse({ functionResponses: responses }); } catch (err) { console.error('[voice] toolResp', err); }
            if (e.voiceEndAfterTurn) armVoiceEndFallback(e, gen);
          }
        },
        onerror: (err: any) => { e.vdbg('ERR: ' + (err && err.message ? err.message : JSON.stringify(err))); },
        onclose: (ev: any) => {
          e.vdbg('closed: ' + (ev && ev.reason ? ev.reason : ''));
          // Gegenseite hat zugemacht (Fehler/Timeout): Sitzung sauber
          // abbauen, sonst bleibt der Knopf für immer rot. Beim normalen
          // Stopp gehört `gen` schon einer alten Generation → kein Doppel-Abbau.
          if (gen !== e.voiceGen) return;
          e.session = null;
          stopVoice(e);
        },
      },
    });
    if (gen !== e.voiceGen) {
      // Stopp kam, während die Verbindung aufgebaut wurde: den frisch
      // geborenen Socket sofort wieder schliessen und NICHT übernehmen —
      // genau dieser Geist redete früher weiter, obwohl der Knopf schon
      // schwarz war.
      try { s.close(); } catch { /* wie zuvor */ }
      return;
    }
    e.session = s;
  } catch (err: any) {
    e.setHint(t('assistant.liveConnectFailed', { detail: err.message }));
    e.micRecording = false; e.micConnecting = false; e.emit();
    stopVoice(e);
  }
}

export function stopVoice(e: AssistantEngine): void {
  if (e.host) e.host.hideModePop();
  // Rest-Transkription sichern, BEVOR die Sitzung abgebaut wird
  try {
    e.flushVoiceAll();
    if (e.currentChatKind === 'voice') e.saveChat('voice');
  } catch { /* Speichern darf das Beenden nie blockieren */ }
  e.notifyHeadless('stopped');
  e.setActivity(null);
  e.headless = false; e.onHeadlessState = null; e.onHeadlessActivity = null;
  // Generation vorrücken: ein noch laufender Verbindungsaufbau oder späte
  // Nachrichten der alten Sitzung erkennen sich daran als überholt.
  e.voiceGen += 1;
  e.voiceActive = false;
  // Bereits eingeplante Audio-Puffer sofort verstummen lassen — das
  // asynchrone playCtx.close() unten reichte dafür nicht («Knopf gedrückt,
  // Stimme redet den Satz trotzdem zu Ende»).
  try { stopPlayback(e); } catch { /* wie zuvor */ }
  // Formular-Flags zurücksetzen: blieb formOpen nach einem Abbruch hängen,
  // war die NÄCHSTE Sitzung stumm (playChunk warf alles weg).
  e.micPaused = false; e.formOpen = false;
  // Verabschiedungs-Zustand mit abräumen — sonst legt ein Timer der ALTEN
  // Sitzung mitten in die nächste hinein auf.
  if (e.voiceEndTimer) { clearTimeout(e.voiceEndTimer); e.voiceEndTimer = null; }
  e.voiceEndAfterTurn = false;
  e.micRecording = false; e.micConnecting = false;
  if (e.rafId) cancelAnimationFrame(e.rafId); e.rafId = null;
  stopScreenWatch(e);
  try { if (e.session) e.session.close(); } catch { /* wie zuvor */ } e.session = null;
  try { if (e.processor) e.processor.disconnect(); } catch { /* wie zuvor */ } e.processor = null;
  try { if (e.micSource) e.micSource.disconnect(); } catch { /* wie zuvor */ } e.micSource = null;
  try { if (e.micStream) e.micStream.getTracks().forEach((tr) => tr.stop()); } catch { /* wie zuvor */ } e.micStream = null;
  try { if (e.audioCtx) e.audioCtx.close(); } catch { /* wie zuvor */ } e.audioCtx = null;
  try { if (e.playCtx) e.playCtx.close(); } catch { /* wie zuvor */ } e.playCtx = null;
  e.playGain = null; // hängt am geschlossenen Kontext — beim nächsten playCtx neu aufbauen
  e.analyser = null; e.voiceOfferId = null;
  if (e.siri) { try { e.siri.dispose(); } catch { /* wie zuvor */ } e.siri = null; }
  const orbWrap = e.host ? e.host.getOrbWrap() : null;
  if (orbWrap) orbWrap.innerHTML = '';
  e.emit();
}
